import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { money, fmtErr } from "../api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Check } from "lucide-react";

export default function PricingPage() {
  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("month");
  const [currency, setCurrency] = useState("GBP");
  const [config, setConfig] = useState({ currencies: ["GBP", "EUR"], fx: { EUR: 1.17 }, default_currency: "GBP" });
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
    api.get("/public/pricing-config").then((r) => {
      setConfig(r.data);
      const region = (navigator.language || "").split("-")[1]?.toUpperCase();
      setCurrency("GBP"); // GBP is the platform standard; users can still toggle.
    }).catch(() => {});
  }, []);

  const rate = (planCurrency) => {
    const from = planCurrency.toUpperCase(), to = currency;
    if (from === to) return 1;
    const fx = config.fx || {};
    if (from === "GBP") return fx[to] ?? null;
    if (to === "GBP") return fx[from] ? 1 / fx[from] : null;
    return fx[from] && fx[to] ? fx[to] / fx[from] : null;
  };

  const subscribe = (plan) => {
    if (!user) return navigate(`/auth?mode=register&role=${plan.role}`);
    setPromo(null);
    setPromoInput("");
    setCheckoutPlan(plan);
  };

  const confirmCheckout = async () => {
    if (!checkoutPlan) return;
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: checkoutPlan.id, origin_url: window.location.origin, interval,
        currency: currency.toLowerCase(),
        promo_code: promo?.code || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(fmtErr(e)); setBusy(false); }
  };

  const applyPromo = async () => {
    if (!promoInput.trim() || !checkoutPlan) return;
    try {
      const { data } = await api.post("/billing/validate-promo", {
        code: promoInput.trim(), plan_id: checkoutPlan.id, interval, currency: currency.toLowerCase(),
      });
      setPromo(data);
      toast.success(`Code ${data.code} applied`);
    } catch (e) { setPromo(null); toast.error(fmtErr(e)); }
  };

  const priceFor = (p) => {
    const base = interval === "year" ? (p.price_yearly ?? p.price * 12) : p.price;
    const r = rate(p.currency);
    return r === null ? null : base * r;
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 relative" data-testid="pricing-page">
      <Seo title="Pricing | CourierRadar" description="Subscription plans for couriers and companies on CourierRadar. Monthly or annual billing, EUR or GBP." />
      <div className="absolute top-14 right-4 sm:right-6 inline-flex items-center bg-white border border-[#E8E4D9] rounded-full p-0.5" data-testid="currency-toggle">
        {(config.currencies || []).map((c) => (
          <button key={c} data-testid={`currency-${c}`} onClick={() => setCurrency(c)}
            className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-all ${currency === c ? "bg-[#1C1B1A] text-white" : "text-[#78756E]"}`}>
            {c === "GBP" ? "£" : "€"} {c}
          </button>
        ))}
      </div>
      <div className="text-center mb-10">
        <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-black">Pricing</h1>
        <p className="text-sm text-zinc-500 mt-3">One plan per account. Cancel anytime.</p>
        <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
          <div className="inline-flex items-center bg-zinc-100 rounded-full p-1" data-testid="interval-toggle">
            {[["month", "Monthly"], ["year", "Annual"]].map(([v, l]) => (
              <button key={v} data-testid={`interval-${v}`} onClick={() => setInterval(v)}
                className={`px-5 py-2 rounded-full text-xs font-semibold transition-all ${interval === v ? "bg-black text-white shadow-sm" : "text-zinc-500"}`}>
                {l}{v === "year" && <span className={`ml-1.5 ${interval === "year" ? "text-zinc-300" : "text-zinc-400"}`}>2 months free</span>}
              </button>
            ))}
          </div>
        </div>
        <p className="text-[11px] text-[#A09D95] mt-3">All prices exclude VAT — VAT is added at checkout where applicable.</p>
      </div>

      {["courier", "company"].map((role) => (
        <div key={role} className="mb-14">
          <h2 className="text-lg font-bold tracking-tight mb-5 capitalize">For {role === "courier" ? "couriers" : "companies"}</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {plans.filter((p) => p.role === role).map((p) => {
              const isCurrent = user && user.subscription_plan_id === p.id;
              const display = priceFor(p);
              return (
                <div key={p.id} data-testid={`plan-${p.id}`}
                  className="border border-zinc-200 rounded-2xl p-6 flex flex-col hover:border-black/30 transition-colors">
                  <h3 className="text-base font-bold">{p.name}</h3>
                  <p className="mt-2">
                    <span className="text-3xl font-extrabold tracking-tight">
                      {display === null ? money(interval === "year" ? (p.price_yearly ?? p.price * 12) : p.price, p.currency.toUpperCase()) : money(display, currency)}
                    </span>
                    <span className="text-xs text-zinc-400"> /{interval === "year" ? "year" : "month"} ex VAT</span>
                  </p>
                  {interval === "year" && display !== null && (
                    <p className="text-[11px] text-zinc-400 mt-0.5">{money(display / 12, currency)}/mo billed annually</p>
                  )}
                  <ul className="mt-4 space-y-2 flex-1">
                    {(p.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-xs text-zinc-600">
                        <Check className="w-3.5 h-3.5 text-black shrink-0 mt-0.5" />{f}
                      </li>
                    ))}
                  </ul>
                  <button data-testid={`subscribe-${p.id}`} onClick={() => subscribe(p)}
                    className={`mt-6 w-full font-semibold transition-colors px-5 py-2.5 rounded-lg text-sm ${isCurrent ? "bg-zinc-100 text-zinc-500 border border-zinc-200" : "bg-black text-white hover:bg-zinc-800"}`}>
                    {isCurrent ? "Current plan" : `Subscribe ${interval === "year" ? "annually" : "monthly"}`}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {plans.length === 0 && <p className="text-center text-sm text-zinc-400">Loading plans…</p>}

      <Dialog open={!!checkoutPlan} onOpenChange={() => setCheckoutPlan(null)}>
        <DialogContent data-testid="checkout-dialog">
          <DialogHeader><DialogTitle>Confirm your plan</DialogTitle></DialogHeader>
          {checkoutPlan && (() => {
            const base = priceFor(checkoutPlan);
            const final = promo && base !== null
              ? Math.max(0, promo.kind === "percent" ? base * (1 - promo.value / 100) : base - promo.discount)
              : base;
            return (
              <div className="space-y-3 mt-2">
                <div className="cr-card !p-4">
                  <p className="text-sm font-bold">{checkoutPlan.name} · {interval === "year" ? "Annual" : "Monthly"}</p>
                  <p className="text-2xl font-extrabold mt-1">
                    {final !== null ? money(final, currency) : money(interval === "year" ? (checkoutPlan.price_yearly ?? checkoutPlan.price * 12) : checkoutPlan.price, checkoutPlan.currency.toUpperCase())}
                    <span className="text-xs font-normal text-[#A09D95]"> ex VAT</span>
                  </p>
                  {promo && base !== null && <p className="text-xs text-[#A09D95] line-through">{money(base, currency)}</p>}
                </div>
                <div className="flex gap-2">
                  <input data-testid="promo-input" placeholder="Promo code (optional)" value={promoInput}
                    onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromo(null); }}
                    className="cr-input !rounded-full !py-2 !text-xs font-mono flex-1" />
                  <button data-testid="promo-apply-btn" onClick={applyPromo} className="cr-btn-outline !py-2 !px-4 !text-xs">Apply</button>
                </div>
                {promo && (
                  <p data-testid="promo-applied" className="cr-pill-yellow w-fit">
                    {promo.code} · {promo.kind === "free_months" ? `${promo.months} months free` : `−${promo.kind === "percent" ? `${promo.value}%` : money(promo.discount, promo.currency)}`}
                  </p>
                )}
                <button data-testid="checkout-confirm-btn" onClick={confirmCheckout} disabled={busy}
                  className="cr-btn-dark w-full !py-3 disabled:opacity-40">
                  {busy ? "Redirecting…" : "Continue to secure payment"}
                </button>
                <p className="text-[11px] text-[#A09D95] text-center">VAT is added at checkout where applicable.</p>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
